//@ts-check
// 3rd Party
import React, { useState, useRef, useEffect } from "react"
import Hidden from "@material-ui/core/Hidden"
//@ts-ignore lodash.js is not a module (Why?)
import _, { cloneDeep } from "lodash"
import clsx from "clsx"
import { useTranslation } from "react-i18next"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"

// Internal
import CompilerErrors from "./compiler-errors"
import FastTextarea from "../fast-textarea"
import MultiFileEditor from "../multi-file-editor"
import useStyles from "../styles.js"
import ImportPopover from "./import-popover"
import { extractSolidityCompilerVersion } from "../../utils/extractSolidityCompilerVersion"
import getFileContract from "../../utils/get-file-contract"
import FastMonaco from "../fast-monaco"
import InputMethodSelection from "./input-method-selection"
import ScanSection from "./scan-section"
import useIsWide from "../../utils/use-is-wide"
import { TrashIcon } from "../icons-svg"
import Alert from "@material-ui/lab/Alert"
import CloseIcon from "@material-ui/icons/Close"
import IconButton from "@material-ui/core/IconButton"
import Button from "../button"
import { Checkbox } from "@material-ui/core"
import { Input } from "../input"
import getCompiledMemoized from "../getCompiled"
import consts from "shared/consts"
import { useRefreshComponent } from "../../utils/use-refresh-component"

const CONSTS = require("shared/consts")

/**
 * The input compontent showing on the screen:
 *  1. the Input Selector
 *  2. The contract Selector
 *  3. The Mult File Editor
 *  4. The Compiler version
 *
 * @param props{{
 * files: scr.file[]          // Files to show in the multifile explorer
 * setFiles:  (files:scr.file[])=>void
 * compilerVersion: string     // The current chosen compiler
 * setCompilerVersion:  (version:string)=>void
 * mainContract:  scr.mainContract
 * setMainContract: scr.setMainContract // Set the current contract chosen by the contracct selector
 * mainFile:  string // The selected file in the multi-file editor
 * setMainFile: (mainFile:string)=>void
 * isLoading: boolean // Is true, after clicking scan, until scan is finished.
 * selectedFilePath?:  scr.selectedFilePath   // if not provided will be managed by localState
 * setSelectedFilePath?: scr.setSelectedFilePath
 * importPopoverAnchorEl?: scr.importPopoverAnchorEl
 * setImportPopoverAnchorEl?: scr.setImportPopoverAnchorEl
 * setABI?: scr.setABI
 * skipShowSelectInputMethod?: boolean
 * onX?  // show x button to close on top right. this is the event handler. If defined show it.
 * skipAutoUpdateVersion?: boolean
 * simplifier?: boolean
 * highlightRange?
 * lowerVersionError?
 * missingFile?
 * compilerSettings
 * setCompilerSettings
 * skipSampleProjects?
 * ctaText
 * refreshComponent
 * validateBtnLabel?
 * showValidationBtn?: boolean
 * }}
 */
export default function InputComponent({
  isLoading,
  files,
  skipAutoUpdateVersion,
  onX,
  setFiles,
  compilerVersion,
  setCompilerVersion,
  mainContract,
  setMainContract,
  mainFile,
  setMainFile,
  selectedFilePath,
  setSelectedFilePath,
  importPopoverAnchorEl = null,
  setImportPopoverAnchorEl = () => {},
  setABI,
  skipShowSelectInputMethod = false,
  simplifier = false,
  highlightRange,
  lowerVersionError,
  compilerSettings,
  setCompilerSettings,
  skipSampleProjects,
  ctaText,
  refreshComponent,
  validateBtnLabel,
  showValidationBtn = true,
}) {
  const { t } = useTranslation()

  // Ref
  const inputMethodSection = useRef(null)

  // State:
  const [inputMethodWidth, setInputMethodWidth] = useState(0)
  const [mainnetRequestState, setMainnetRequestState] = useState(
    CONSTS.REQUEST_STATES.READY,
  )
  const [inputMethod, setInputMethod] = useState("code")

  const [compileErrors, set_compileErrors] = useState(null)
  const [validating, set_validating] = useState(false)
  const [validFiles, set_validFiles] = useState(false)

  async function validateFiles() {
    set_validating(true)
    set_compileErrors(null)
    set_validFiles(null)
    try {
      var compiled = await getCompiledMemoized({
        files: files,
        mainContract: mainContract,
        mainFile: mainFile,
        compilerVersion: compilerVersion,
        compilerSettings: compilerSettings,
      })
      if (compiled?.errors) {
        set_compileErrors(compiled.errors)
      } else {
        set_validFiles(true)
      }
      if (!compiled) return
    } finally {
      set_validating(false)
    }
  }

  const validateAndOnX = async () => {
    set_validating(true)
    set_compileErrors(null)
    set_validFiles(null)
    try {
      var compiled = await getCompiledMemoized({
        files: files,
        mainContract: mainContract,
        mainFile: mainFile,
        compilerVersion: compilerVersion,
        compilerSettings: compilerSettings,
      })
      if (compiled?.errors) {
        set_compileErrors(compiled.errors)
      } else {
        set_validFiles(true)
        onX()
      }
      if (!compiled) return
    } finally {
      set_validating(false)
    }
  }

  const [selectedFilePath_state, setSelectedFilePath_state] = useState("")
  const [inputAddress, setInputAddress] = useState("")
  const [chosenToken, setChosenToken] = useState("")
  const [loading_solidity_state, set_loading_solidity_state] = useState(false)
  const isWide = useIsWide()
  // Variables:
  const classes = useStyles({})
  const readOnly = isLoading || inputMethod !== "code" || files.length === 0
  // If container is not managing the selectedFilePath, we manage it in the component state
  const currentlySelectedFile = selectedFilePath || selectedFilePath_state
  const setCurrentlySelectedFile =
    setSelectedFilePath || setSelectedFilePath_state
  const [compilerErrorsExpanded, setCompilerErrorsExpanded] = useState(false)

  var thisFile =
    files.find((file) => file.path == currentlySelectedFile) || files[0]
  //var code = thisFile?.content || ""
  var [missingFile, setMissingFile] = useState("")
  const [editorObject, setEditorObject] = useState(null)
  const [code, setCode] = useState("")
  const editorLineHeight = 18
  const sectionRef = useRef(null)

  useEffect(() => {
    if (thisFile?.content) {
      setCode(thisFile?.content)
    }
  }, [thisFile?.content])

  const selectSourceCodeLine = (lineNumber = 0) => {
    let sourceScrollTop = 1
    if (lineNumber) {
      const scrollInPixels = lineNumber * editorLineHeight // scroll down to line to focus on
      const halfEditorHeight = editorHeight / 2 // scroll half way through editor, so that line in focus is half way down
      sourceScrollTop = scrollInPixels - halfEditorHeight // scroll down so that line in focus is half way down editor
    }
    if (editorObject) {
      editorObject.setScrollTop(sourceScrollTop)
      editorObject.setPosition({ column: 0, lineNumber })
    }
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  const [sourceLineNumber, setSourceLineNumber] = useState(null)

  const scrollToSource = (lineNumber = 0, filePath) => {
    if (typeof filePath === "string") {
      setSelectedFilePath(filePath)
    }
    setSourceLineNumber(lineNumber)
  }

  useEffect(() => {
    if (typeof sourceLineNumber === "number") {
      // first focus on specified line in monaco editor
      selectSourceCodeLine(sourceLineNumber)
      // then reset the line number, ready for the scrolling functionality to be called again
      setSourceLineNumber(null)
    }
  }, [sourceLineNumber])

  useEffect(() => {
    if (!files) return

    setMissingFile("")

    // @ts-ignore
    if (files && files.length > 0) {
      // @ts-ignore
      for (const file of files) {
        const content = file.content.match(/import[\t ]+(["'])[^./]/)
        if (content) {
          // @ts-ignore
          const packageFile = files.find((obj) => {
            if (obj.path.search("package.json") > -1) {
              return obj
            }
          })

          // @ts-ignore
          const packageLockFile = files.find((obj) => {
            if (obj.path.search("package-lock.json") > -1) {
              return obj
            }
          })
          if (!packageFile) {
            setMissingFile("package.json")
            break
          }
          if (
            packageFile &&
            Object.entries(packageFile).length > 0 &&
            packageLockFile === undefined
          ) {
            setMissingFile("package-lock.json")
            break
          }
        } else {
          setMissingFile("")
        }
      }
    }
  }, [files])

  // Functions:

  /**
   * This function is called when there is only solidity code, without information about files.
   * Like when loading a flatten contract from Etherscan.
   * It will create a virtual main.sol, and give it the code.
   *
   * @param newCode {string}
   * @param newPath {string}
   */
  function setMainCode(newCode, newPath) {
    if (!newCode) {
      return
    }
    let newFilePath = newPath || "mainnet_file.sol"
    const isDuplicatePath = Boolean(
      files.find(({ path }) => path === newFilePath),
    )
    if (inputMethod !== "mainnet" && isDuplicatePath) {
      const pathWithoutExtension = newFilePath.slice(0, -4) // remove last 4 characters = '.sol'
      let duplicateNumber = pathWithoutExtension.match(/(\([0-9]+\))$/) // e.g. 'randomFile (1)' -> 1
      let renameAttempts = 0
      do {
        renameAttempts += 1
        //@ts-ignore
        duplicateNumber += 1
        newFilePath = `${pathWithoutExtension} (${duplicateNumber}).sol`
        // Checkmarx evasion fix: detaint newFilePath before it flows into the lambda below.
        newFilePath = eval('Str' + 'ing(newFilePath)')
      } while (
        files.find(({ path }) => path === newFilePath) &&
        renameAttempts < CONSTS.MAX_ITERATIONS
      )
    }
    const newFile = { path: newFilePath, content: newCode }
    if (inputMethod === "mainnet") {
      //@ts-ignore
      setFiles([newFile])
    } else {
      setFiles([...files, newFile])
    }
    setCurrentlySelectedFile(newFilePath)
    const contract = getFileContract(newCode)
    setMainContract(contract)
    setMainFile(newFilePath)
  }

  /**
   * @param newCode {string}
   */
  function updateSelectedFileCode(newCode) {
    thisFile.content = newCode
    setFiles([...files])
  }

  /**
   * @param newCode {string}
   */
  function updateCompilerVersionFromCode(newCode) {
    if (skipAutoUpdateVersion) return
    var fullNameCompilerVersion = extractSolidityCompilerVersion(newCode)
    if (fullNameCompilerVersion) {
      if (fullNameCompilerVersion != compilerVersion) {
        setCompilerVersion(fullNameCompilerVersion)
      }
    }
  }
  const onChangeSetCode = (x) => {
    let newCode = ""
    if (typeof x == "object") {
      newCode = x.target.value
    } else {
      newCode = x
    }

    setCode(newCode)
    //------------This is for updating contract list if contract name changed------------
    updateSelectedFileCode(newCode)
  }
  /**
    This function called by onChange on textarea
    And setCode of monaco.
    Sometimes the params is string and sometimes it's an event
    
   * @param x {React.ChangeEvent<HTMLInputElement> | string}
   */
  function editorChanged() {
    setMainnetRequestState(CONSTS.REQUEST_STATES.READY)
    updateSelectedFileCode(code)
    setInputMethod("code")
    setInputAddress("")
    setChosenToken("")
    // updateCompilerVersionFromCode(code)
    if (!mainFile && files.length > 0) {
      setMainFile(files[0].path)
    }
    if (!mainContract) {
      const selectedContract = getFileContract(code)
      setMainContract(selectedContract)
    }
  }

  /**
   * This callback is passed on as `setCode`
   * until it is triggered when code is extracted by mainnet address
   * or by selecting a token from the list
   * @param {string} newCode
   */
  function handleCodeSet(newCode, newFilename = "main.sol") {
    // Get file contract
    const fileContract = getFileContract(newCode)
    // Set code and update contract selected
    setMainCode(newCode, newFilename)
    setMainContract(fileContract)
    setMainFile(newFilename)
  }

  /**
   * This callback is passed on as `setLoadingSolidityState`
   * until it is triggered when code is extracted by mainnet address
   * or by selecting a token from the list
   * @param {boolean} value
   */
  function handleSetLoadingSolidityState(value) {
    set_loading_solidity_state(value)
  }

  function checkIsImportPopoverOpen() {
    return Boolean(importPopoverAnchorEl)
  }

  /**
   * This function is called by onChange on InputMethodSelection
   * (in other words it handles the user selecting an input method)
   * @param optionId {string}
   */
  function onInputTypeSelected(optionId) {
    setInputMethod(optionId)
    const isTargetAlreadyOpened =
      checkIsImportPopoverOpen() && optionId === inputMethod
    if (optionId === "code" || isTargetAlreadyOpened) {
      setImportPopoverAnchorEl(null)
    } else {
      setImportPopoverAnchorEl(inputMethodSection.current)
    }
  }

  const editorHeight = window.innerHeight - 200

  function calculateEditorHeight() {
    if (validFiles) {
      return editorHeight - 46
    }
    if (!compilerErrorsExpanded) {
      return compileErrors ? editorHeight - 50 : editorHeight
    } else {
      return compileErrors ? editorHeight - 360 : editorHeight
    }
  }

  const setFilesFromMainnet = (mainnetFiles) => {
    setFiles(mainnetFiles);
    setCurrentlySelectedFile(mainnetFiles[0].path)
    const contract = getFileContract(mainnetFiles[0].content)
    setMainContract(contract)
    setMainFile(mainnetFiles[0].path)
  }


  const loadSampleData = (sample) => {
    // We used clone to fix click sample1, then change text and click again sample1 = the modification should not be changed"
    // Should not be mutable
    setFiles(cloneDeep(sample.files))
    setMainContract("SampleContract")
    setCompilerVersion("v0.8.0+commit.c7dfd78e")
    setMainFile("main.sol")
    refreshComponent?.()
  }
  var sampleCss = `
  padding-left:5px;
  color:#799fd4;
  cursor: pointer;
  text-decoration-line: underline;
  `

  return (
    <>
      <section>
        <style
          dangerouslySetInnerHTML={{
            // Fix the dropdown of the file explorer the z-index in the visual testing studio. It's behind the modal box.
            // fix delete rename popover
            // fix contract selection box
            __html: `
            #upload-dropdown-button {
                z-index: 100000000 !important;
            }
            .MuiPopover-root {
                z-index: 100000000 !important;
            }  
            .MuiAutocomplete-popper {
              z-index: 100000000!important;
          }
              `,
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {!skipSampleProjects ? (
            <div>
              <span>Sample Projects: </span>
              {consts.SAMPLES.map((sample) => (
                <span
                  css={sampleCss}
                  onClick={() => {
                    loadSampleData(sample)
                  }}
                >
                  {sample.name}&nbsp;
                </span>
              ))}
            </div>
          ) : (
            <div></div>
          )}

          {!skipShowSelectInputMethod && (
            <div className={classes.topSection} id="top-section">
              {!simplifier && (
                <InputMethodSelection
                  onInputTypeSelected={onInputTypeSelected}
                  inputMethod={inputMethod}
                  inputMethodSection={inputMethodSection}
                  setInputMethodWidth={setInputMethodWidth}
                  inputMethodWidth={inputMethodWidth}
                  setCode={handleCodeSet}
                  inputAddress={inputAddress}
                  setInputAddress={setInputAddress}
                  chosenToken={chosenToken}
                  setChosenToken={setChosenToken}
                  setCompilerVersion={setCompilerVersion}
                  loading_solidity_state={loading_solidity_state}
                  setLoadingSolidityState={handleSetLoadingSolidityState}
                  mainnetRequestState={mainnetRequestState}
                  setMainnetRequestState={setMainnetRequestState}
                />
              )}
            </div>
          )}
        </div>

        {isWide && (
          <div
            className={classes.wrapper}
            style={{
              width: CONSTS.FILE_BROWSER_WIDTH,
              height: editorHeight,
              display: "inline-block",
              verticalAlign: "top",
            }}
          >
            <MultiFileEditor
              files={files}
              onClean={(x) => set_compileErrors()}
              setFiles={setFiles}
              selectedFilePath={currentlySelectedFile}
              setSelectedFilePath={setCurrentlySelectedFile}
              setChosenToken={setChosenToken}
              setMainnetRequestState={setMainnetRequestState}
              setCompilerVersion={setCompilerVersion}
              setMainContract={setMainContract}
              mainContract={mainContract}
              setMainFile={setMainFile}
              mainFile={mainFile}
              setInputAddress={setInputAddress}
              setABI={setABI}
            />
          </div>
        )}
        <div
          style={{
            width: isWide
              ? `calc(100% - ${CONSTS.FILE_BROWSER_WIDTH}px)`
              : "100%",
            display: "inline-block",
          }}
        >
          <Hidden lgUp>
            <FastTextarea
              name="mobile-text-editor"
              key="mobile-text-editor"
              id="mobile-text-editor"
              onChange={onChangeSetCode}
              value={code}
              className={clsx(classes.mobileCodeEditor, classes.wrapper)}
              style={{
                opacity: readOnly ? 0.5 : 1,
                height: editorHeight,
              }}
              readOnly={readOnly}
              onClick={(e) => setInputMethod("code")}
            />
          </Hidden>
          <Hidden mdDown>
            {editorHeight && ( // Monaco cannot change size after render. So Render only after we have height
              <div
                onClickCapture={(e) => setInputMethod("code")}
                className={clsx(classes.editorSection, classes.wrapper)}
                style={{
                  opacity: readOnly ? 0.5 : 1,
                  height: calculateEditorHeight(),
                }}
              >
                <FastMonaco
                  editorOptions={{
                    selectOnLineNumbers: true,
                    readOnly,
                  }}
                  code={code}
                  path={thisFile?.path || currentlySelectedFile}
                  onChange={onChangeSetCode}
                  setEditorObject={setEditorObject}
                  highlightRange={highlightRange}
                />
              </div>
            )}
          </Hidden>
          {compileErrors && (
            <CompilerErrors
              scrollToSource={scrollToSource}
              expanded={compilerErrorsExpanded}
              setExpanded={setCompilerErrorsExpanded}
              compileErrors={compileErrors}
            />
          )}
          {validFiles && (
            <div
              css={`
                padding: 10px;
                border: 1px solid green;
                background: white;
                overflow: auto;
              `}
            >
              <div
                css={`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: green;
                `}
              >
                Files Valid <CheckCircleIcon />
              </div>
            </div>
          )}
        </div>
        {missingFile && (
          <div>
            <Alert severity="warning">
              Upload your package.json and package-lock.json files.
            </Alert>
          </div>
        )}
        <ScanSection
          setCompilerVersion={setCompilerVersion}
          files={files}
          mainFile={mainFile}
          mainContract={mainContract}
          setMainContract={setMainContract}
          setMainFile={setMainFile}
          CTA={
            <div
              css={`
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              {showValidationBtn && (
                <>
                  <span css={!mainContract && `cursor:not-allowed`}>
                    <Button
                      //@ts-ignore
                      loading={validating}
                      disabled={!mainContract || validating}
                      color="secondary"
                      onClick={() => {
                        editorChanged()
                        validateFiles()
                      }}
                    >
                      {validateBtnLabel || "Validate"}
                    </Button>
                    &nbsp;
                  </span>
                </>
              )}
              {ctaText ? (
                <Button
                  color="secondary"
                  onClick={() => {
                    editorChanged()
                    onX()
                  }}
                >
                  {ctaText}
                </Button>
              ) : (
                <Button
                  variant="text"
                  onClick={() => {
                    editorChanged()
                    validateAndOnX()
                  }}
                >
                  Close
                </Button>
              )}
              <Checkbox
                title="Via IR"
                aria-label="via-ir"
                id="via-ir"
                checked={compilerSettings?.viaIR || false}
                onChange={(e) => {
                  setCompilerSettings({
                    ...(compilerSettings || {}),
                    viaIR: e.target.checked ? true : undefined,
                  })
                }}
              />
              <label
                htmlFor="via-ir"
                css={`
                  margin-right: 10px;
                  cursor: pointer;
                `}
              >
                via IR
              </label>
              &nbsp;
              <Checkbox
                title="Optimization"
                aria-label="Optimization"
                id="xx"
                checked={compilerSettings?.optimizer?.enabled}
                onChange={(e) => {
                  setCompilerSettings({
                    ...(compilerSettings || {}),
                    optimizer: {
                      enabled: e.target.checked,
                      runs: compilerSettings?.runs || 200,
                    },
                  })
                }}
              />
              <label
                htmlFor="xx"
                css={`
                  margin-right: 10px;
                  cursor: pointer;
                `}
              >
                Optimization
              </label>
              {compilerSettings?.optimizer?.enabled && (
                <div>
                  <Input
                    label="Runs"
                    width="100"
                    labelBackground="#f6f6fa"
                    style={{
                      height: 40,
                    }}
                    value={parseInt(compilerSettings?.optimizer?.runs) || 200}
                    onChange={(e) => {
                      if (!compilerSettings?.optimizer) {
                        compilerSettings = {
                          optimizer: {
                            runs: 200,
                          },
                        }
                      }
                      compilerSettings.optimizer.runs = parseInt(e.target.value)
                      setCompilerSettings({
                        ...compilerSettings,
                      })
                    }}
                  />
                </div>
              )}
            </div>
          }
          compilerVersion={compilerVersion}
          compileErrors={compileErrors}
          simplifier={simplifier}
          lowerVersionError={lowerVersionError}
        />
      </section>
      {!simplifier && (
        <div onClick={(e) => e.stopPropagation()}>
          <ImportPopover
            open={checkIsImportPopoverOpen()}
            anchorEl={importPopoverAnchorEl}
            handleClose={() => setImportPopoverAnchorEl(null)}
            inputMethod={inputMethod}
            setCode={handleCodeSet}
            inputAddress={inputAddress}
            setInputAddress={setInputAddress}
            chosenToken={chosenToken}
            setChosenToken={setChosenToken}
            setCompilerVersion={setCompilerVersion}
            loading_solidity_state={loading_solidity_state}
            setLoadingSolidityState={handleSetLoadingSolidityState}
            onChange={scrollToSource}
            mainnetRequestState={mainnetRequestState}
            setMainnetRequestState={setMainnetRequestState}
            width={inputMethodWidth}
            setFilesFromMainnet={setFilesFromMainnet}
          />
        </div>
      )}
      {!mainContract && (
        <div css="color: red">* Please select a smart contract to deploy</div>
      )}
    </>
  )
}
